import breakpointTokens from '@hz-design-system/design-tokens/dist/web/global/breakpoints.json';

import styleProps from './styleProps';
import { pickAlias } from './aliases';
import camelcase from 'camelcase-es5';
import { TBreakpointsKeyValue } from '../../types/TBreakpointsKeyValue';
import getUtilityClassName from '../className/getUtilityClassName';
import { TStyleProps } from '../../types/TStyleProps';
import { TBreakpoint } from '../../types/TBreakpoint';

const breakpoints = Object.keys(breakpointTokens);

type TOptions = {
  isResponsive?: boolean;
  asClassNames?: boolean;
};

const defaultOptions: TOptions = {
  isResponsive: true,
  asClassNames: false,
};

type TGetUtil = {
  key: string;
  value: string;
  breakpoint?: TBreakpoint;
};

const getUtil = ({ key, value, breakpoint }: TGetUtil, options: TOptions): string => {
  let util: string;
  util = camelcase(`${key}-${value}`);
  if (options.isResponsive) {
    util = breakpoint ? `${breakpoint}-${util}` : util;
  }
  if (options.asClassNames) {
    util = getUtilityClassName(util);
  }
  return util;
};

const getUtilFromString = (key: string, value: string, options: TOptions): string[] => [
  getUtil({ key, value }, options),
];

/**
 * Breakpoint values are derived from array position
 * [xs (default), s, m, l, xl]
 * @param key
 * @param values
 * @param options
 */
const getUtilsFromArray = (key: string, values: (string | null)[], options: TOptions): string[] => {
  const utils: string[] = [];
  for (let j = 0; j < values.length; j++) {
    const value = values[j];
    if (value === null) {
      continue;
    }
    let util: string;
    if (j === 0) {
      // Default style (xs mobile-first)
      util = getUtil({ key, value }, options);
    } else {
      util = getUtil({ key, value, breakpoint: breakpoints[j] as TBreakpoint }, options);
    }
    utils.push(util);
  }
  return utils;
};

/**
 * Breakpoint values are derived from object key/value
 * { xs (default), s, m, l, xl }
 * @param key
 * @param value
 * @param options
 */
const getUtilsFromObject = (key: string, value: TBreakpointsKeyValue, options: TOptions): string[] => {
  const utils: string[] = [];
  const keys = Object.keys(value);
  for (let j = 0; j < keys.length; j++) {
    const breakpoint = keys[j];
    const breakpointValue = value[breakpoint];
    let util: string;
    if (breakpoint === 'xs') {
      // Default style (mobile-first)
      util = getUtil({ key, value: breakpointValue }, options);
    } else {
      util = getUtil(
        {
          key,
          value: breakpointValue,
          breakpoint: breakpoint as TBreakpoint,
        },
        options,
      );
    }
    utils.push(util);
  }
  return utils;
};

/**
 * Box helper fn
 * Parse each key/value utility
 * @param utils: TUtilsObj
 * @param userOptions: TOptions
 */
const withStyleProps = (utils?: TStyleProps, userOptions?: TOptions): string[] => {
  if (!utils) {
    return [];
  }
  const options = { ...defaultOptions, ...userOptions };
  const keys = Object.keys(utils);
  return keys.reduce((acc: string[], key: string): string[] => {
    if (!styleProps[key]) {
      return acc;
    }
    const value = utils[key];
    const name = pickAlias(key);
    if (typeof value === 'string') {
      return acc.concat(getUtilFromString(name, value, options));
    }
    if (Array.isArray(value)) {
      return acc.concat(getUtilsFromArray(name, value, options));
    }
    if (typeof value === 'object') {
      return acc.concat(getUtilsFromObject(name, value, options));
    }
    return acc;
  }, []);
};

export default withStyleProps;
